import { ApiService } from './api';
import { Invoice, CreateInvoiceData, UpdateInvoiceData } from '@/models/invoice';
import { ApiResponse } from '@/models/common';
import { getCachedInvoices, cacheInvoices, getCachedNextInvoiceNumber, cacheNextInvoiceNumber, clearInvoicesCache } from '@/utils/sessionManager';
import { ApiEndpoints, Endpoints } from '@/models/endpoints';

export class BillService {
  // Get all bills (no filters, returns total data)
  static async getBills(): Promise<ApiResponse<Invoice[]>> {
    const response = await ApiService.get<Invoice[]>(ApiEndpoints.BILLS);
    return response;
  }

  // Get bill by ID
  static async getBillById(id: string): Promise<ApiResponse<Invoice>> {
    return ApiService.get<Invoice>(Endpoints.getBill(id));
  }

  // Get bill by ID without duplicate detection (for retries)
  static async getBillByIdWithoutDuplicateCheck(id: string): Promise<ApiResponse<Invoice>> {
    return ApiService.getWithoutDuplicateCheck<Invoice>(Endpoints.getBill(id));
  }

  // Get bill by bill number
  static async getBillByNumber(billNumber: string): Promise<ApiResponse<Invoice>> {
    return ApiService.get<Invoice>(Endpoints.getBillByNumber(billNumber));
  }

  // Create new bill
  static async createBill(billData: CreateInvoiceData): Promise<ApiResponse<Invoice>> {
    const response = await ApiService.post<Invoice>(ApiEndpoints.BILLS, billData);
    if (response.success) {
      // Clear cache when new bill is created
      clearInvoicesCache();
    }
    return response;
  }

  // Update bill
  static async updateBill(id: string, billData: UpdateInvoiceData): Promise<ApiResponse<Invoice>> {
    const response = await ApiService.put<Invoice>(Endpoints.updateBill(id), billData);
    if (response.success) {
      // Clear cache when bill is updated
      clearInvoicesCache();
    }
    return response;
  }

  // Delete bill (hard delete using DELETE method)
  static async deleteBill(id: string): Promise<ApiResponse<{ message: string }>> {
    return ApiService.delete<{ message: string }>(Endpoints.deleteBill(id));
  }

  // Search bills by various criteria (client-side filtering)
  static async searchBills(query: string): Promise<ApiResponse<Invoice[]>> {
    return ApiService.get<Invoice[]>(Endpoints.searchBills(query));
  }

  // Get bills for dropdown/autocomplete (only non-deleted)
  static async getBillsForSelect(): Promise<ApiResponse<Invoice[]>> {
    return ApiService.get<Invoice[]>(ApiEndpoints.BILLS_SELECT);
  }

  // Bulk delete bills (using multiple single delete calls)
  static async bulkDeleteBills(ids: string[]): Promise<ApiResponse<{ message: string; deletedCount: number }>> {
    const results = [];
    let successCount = 0;
    let errorCount = 0;
    
    // Process deletions sequentially to avoid overwhelming the server
    for (const id of ids) {
      try {
        const result = await this.deleteBill(id);
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
        }
        results.push({ id, success: result.success, error: result.error });
      } catch (error) {
        errorCount++;
        results.push({ id, success: false, error: error instanceof Error ? error.message : 'Unknown error' });
      }
    }
    
    // Clear cache after bulk delete
    clearInvoicesCache();
    
    return {
      success: errorCount === 0,
      data: {
        message: `Bulk delete completed. ${successCount} successful, ${errorCount} failed.`,
        deletedCount: successCount
      },
      message: errorCount === 0 ? 'All bills deleted successfully' : `${errorCount} bills failed to delete`
    };
  }

  // Get next bill number
  static async getNextBillNumber(): Promise<ApiResponse<{ nextNumber: string }>> {
    // Check cache first
    const cachedNumber = getCachedNextInvoiceNumber();
    if (cachedNumber) {
      return {
        success: true,
        data: { nextNumber: cachedNumber as string },
        message: 'Loaded from cache'
      };
    }

    // Since the next-number API doesn't exist, we'll generate a simple sequence
    // In a real implementation, this would be handled by the backend
    try {
      // Get all bills to find the highest number
      const response = await ApiService.get<Invoice[]>(ApiEndpoints.BILLS);
      if (response.success) {
        const bills = response.data;
        let maxNumber = 0;
        
        // Find the highest bill number
        bills.forEach(bill => {
          const match = bill.invoice_number.match(/^(\d+)\//);
          if (match) {
            const num = parseInt(match[1]);
            if (num > maxNumber) {
              maxNumber = num;
            }
          }
        });
        
        // Generate next number
        const nextNumber = maxNumber + 1;
        const currentYear = new Date().getFullYear();
        const financialYear = `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
        const nextBillNumber = `${nextNumber}/${financialYear}`;
        
        // Cache the result
        cacheNextInvoiceNumber(nextBillNumber);
        
        return {
          success: true,
          data: { nextNumber: nextBillNumber },
          message: 'Success'
        };
      }
      
      // If no bills exist, start with 1
      const currentYear = new Date().getFullYear();
      const financialYear = `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
      const nextBillNumber = `1/${financialYear}`;
      
      // Cache the result
      cacheNextInvoiceNumber(nextBillNumber);
      
      return {
        success: true,
        data: { nextNumber: nextBillNumber },
        message: 'Success'
      };
    } catch (error) {
      // Fallback to a simple number if API fails
      const currentYear = new Date().getFullYear();
      const financialYear = `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
      const nextBillNumber = `1/${financialYear}`;
      
      // Cache the result
      cacheNextInvoiceNumber(nextBillNumber);
      
      return {
        success: true,
        data: { nextNumber: nextBillNumber },
        message: 'Generated fallback number'
      };
    }
  }

  // Check if bill number exists
  static async checkBillNumberExists(billNumber: string): Promise<ApiResponse<{ exists: boolean }>> {
    try {
      // Check cache first
      const cachedInvoices = getCachedInvoices();
      if (cachedInvoices) {
        const exists = (cachedInvoices as Invoice[]).some((invoice: Invoice) => invoice.invoice_number === billNumber);
        return {
          success: true,
          data: { exists },
          message: 'Checked from cache'
        };
      }

      // Get all bills and check if the number exists
      const response = await ApiService.get<Invoice[]>(ApiEndpoints.BILLS);
      if (response.success) {
        const bills = response.data;
        const exists = bills.some(bill => bill.invoice_number === billNumber);
        
        // Cache the bills for future use
        cacheInvoices(bills);
        
        return {
          success: true,
          data: { exists },
          message: 'Success'
        };
      }
      
      return {
        success: false,
        data: { exists: false },
        error: response.error || 'Failed to check bill number'
      };
    } catch (error) {
      return {
        success: false,
        data: { exists: false },
        error: 'Failed to check bill number'
      };
    }
  }

  // Export bills
  static async exportBills(): Promise<Blob> {
    const response = await fetch(ApiEndpoints.BILLS_EXPORT, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to export bills');
    }
    
    return response.blob();
  }

  // Generate bill PDF
  static async generateBillPDF(id: string): Promise<Blob> {
    const response = await fetch(`/api/bills/${id}/pdf`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to generate bill PDF');
    }
    
    return response.blob();
  }

  // Send bill via email
  static async sendBillEmail(id: string, emailData: {
    to: string;
    subject?: string;
    message?: string;
  }): Promise<ApiResponse<{ message: string }>> {
    return ApiService.post<{ message: string }>(Endpoints.sendBillEmail(id), emailData);
  }

  // Get bill statistics
  static async getBillStats(): Promise<ApiResponse<{
    totalBills: number;
    totalAmount: number;
    averageAmount: number;
    pendingBills: number;
    paidBills: number;
    overdueBills: number;
  }>> {
    return ApiService.get(ApiEndpoints.BILLS_STATS);
  }

  // Get HSN summary for bills
  static async getHSNSummary(): Promise<ApiResponse<{
    hsnCode: string;
    totalAmount: number;
    cgstTotal: number;
    sgstTotal: number;
    igstTotal: number;
    billCount: number;
  }[]>> {
    return ApiService.get(ApiEndpoints.BILLS_HSN_SUMMARY);
  }

  // Duplicate bill
  static async duplicateBill(id: string): Promise<ApiResponse<Invoice>> {
    return ApiService.post<Invoice>(Endpoints.duplicateBill(id));
  }

  // Mark bill as paid
  static async markAsPaid(id: string, paymentData?: {
    paymentDate?: string;
    paymentMethod?: string;
    reference?: string;
  }): Promise<ApiResponse<Invoice>> {
    return ApiService.put<Invoice>(Endpoints.markBillPaid(id), paymentData);
  }

  // Mark bill as pending
  static async markAsPending(id: string): Promise<ApiResponse<Invoice>> {
    return ApiService.put<Invoice>(Endpoints.markBillPending(id));
  }

  // Add note to bill
  static async addNote(id: string, note: string): Promise<ApiResponse<Invoice>> {
    return ApiService.put<Invoice>(Endpoints.updateBillNote(id), { note });
  }

  // Get bill history/audit trail
  static async getBillHistory(id: string): Promise<ApiResponse<{
    action: string;
    timestamp: string;
    user: string;
    details: any;
  }[]>> {
    return ApiService.get(Endpoints.getBillHistory(id));
  }
} 